import React, { useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { memberListing } from "src/DAL/member/member";
import { programme_minimal_list } from "src/DAL/Programme/Programme";
import { getTrackersApi } from "src/DAL/tracker/tracker";

export const AUDIENCE_TYPES = [
  { value: "members", label: "Specific members" },
  { value: "tracker", label: "By journey" },
  { value: "programme", label: "By programme" },
  { value: "all", label: "All members" },
];

const TRACKER_STATUS_OPTIONS = [
  { value: "active", label: "Active in journey" },
  { value: "left", label: "Left the journey" },
  { value: "all", label: "Everyone who joined" },
];

const MEMBER_SEARCH_LIMIT = 20;
const MEMBER_SELECT_MAX = 500;
const SEARCH_DEBOUNCE_MS = 400;

const escapeRegExp = (value) =>
  String(value || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const memberName = (member) =>
  `${member?.first_name || ""} ${member?.last_name || ""}`.trim() ||
  member?.email ||
  "Unnamed member";

/**
 * Builds the API `target` from the selector state, or null while the
 * chosen audience is still incomplete.
 */
export const buildAudienceTarget = (audience) => {
  const { type, members, tracker, trackerStatus, programme } = audience;
  if (type === "members") {
    return members.length
      ? { type, memberIds: members.map((member) => member._id) }
      : null;
  }
  if (type === "tracker") {
    return tracker ? { type, trackerId: tracker._id, trackerStatus } : null;
  }
  if (type === "programme") {
    return programme ? { type, programmeId: programme._id } : null;
  }
  return { type: "all" };
};

export const INITIAL_AUDIENCE = {
  type: "members",
  members: [],
  tracker: null,
  trackerStatus: "active",
  programme: null,
};

const useOptionsList = (loader) => {
  const [state, setState] = useState({ items: [], loading: true, error: "" });
  useEffect(() => {
    let cancelled = false;
    loader().then((result) => {
      if (cancelled) return;
      setState(result);
    });
    return () => {
      cancelled = true;
    };
  }, [loader]);
  return state;
};

const loadTrackers = async () => {
  const result = await getTrackersApi({
    limit: 500,
    sortBy: "name",
    sortDir: "asc",
  });
  if (result.code !== 200) {
    return {
      items: [],
      loading: false,
      error: result.message || "Couldn't load journeys",
    };
  }
  return { items: result.trackers || [], loading: false, error: "" };
};

const loadProgrammes = async () => {
  const result = await programme_minimal_list();
  if (result.code !== 200) {
    return {
      items: [],
      loading: false,
      error: result.message || "Couldn't load programmes",
    };
  }
  const items = [...(result.programs || [])].sort((a, b) =>
    String(a.title || "").localeCompare(String(b.title || ""))
  );
  return { items, loading: false, error: "" };
};

function MemberPicker({ value, onChange }) {
  const [input, setInput] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const requestRef = useRef(0);

  useEffect(() => {
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    setLoading(true);
    const timer = setTimeout(async () => {
      const result = await memberListing(0, MEMBER_SEARCH_LIMIT, {
        search_text: escapeRegExp(input.trim()),
      });
      if (requestRef.current !== requestId) return;
      setLoading(false);
      if (result.code === 200) {
        setOptions(result.member || []);
        setError("");
      } else {
        setOptions([]);
        setError(result.message || "Couldn't search members");
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [input]);

  const atLimit = value.length >= MEMBER_SELECT_MAX;

  return (
    <Autocomplete
      multiple
      filterSelectedOptions
      options={options}
      value={value}
      loading={loading}
      filterOptions={(items) => items}
      getOptionLabel={memberName}
      isOptionEqualToValue={(option, selected) => option._id === selected._id}
      getOptionDisabled={() => atLimit}
      onChange={(event, selected) => onChange(selected)}
      onInputChange={(event, text, reason) => {
        if (reason === "input" || reason === "clear") setInput(text);
      }}
      renderOption={(props, option) => (
        <li {...props} key={option._id}>
          <Box>
            <Typography sx={{ fontSize: 14 }}>{memberName(option)}</Typography>
            {option.email ? (
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                {option.email}
              </Typography>
            ) : null}
          </Box>
        </li>
      )}
      noOptionsText={error || "No members found"}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Members"
          placeholder="Search by name or email"
          helperText={
            atLimit
              ? `You can select up to ${MEMBER_SELECT_MAX} members`
              : `${value.length} selected`
          }
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

function SingleOptionPicker({ label, list, value, onChange, getLabel }) {
  return (
    <Autocomplete
      options={list.items}
      value={value}
      loading={list.loading}
      getOptionLabel={getLabel}
      isOptionEqualToValue={(option, selected) => option._id === selected._id}
      onChange={(event, selected) => onChange(selected)}
      noOptionsText={list.error || "Nothing found"}
      renderOption={(props, option) => (
        <li {...props} key={option._id}>
          {getLabel(option)}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          error={Boolean(list.error)}
          helperText={list.error || ""}
        />
      )}
    />
  );
}

const trackerLabel = (tracker) =>
  tracker?.status && tracker.status !== "live" && tracker.status !== "published"
    ? `${tracker.name} (${tracker.status})`
    : tracker?.name || "";

export default function AudienceSelector({ audience, onChange, disabled }) {
  const trackers = useOptionsList(loadTrackers);
  const programmes = useOptionsList(loadProgrammes);
  const update = (patch) => onChange({ ...audience, ...patch });

  return (
    <Box sx={{ opacity: disabled ? 0.6 : 1, pointerEvents: disabled ? "none" : "auto" }}>
      <Typography sx={{ fontWeight: 600, mb: 0.5 }}>Audience</Typography>
      <RadioGroup
        row
        value={audience.type}
        onChange={(event) => update({ type: event.target.value })}
      >
        {AUDIENCE_TYPES.map((option) => (
          <FormControlLabel
            key={option.value}
            value={option.value}
            control={<Radio />}
            label={option.label}
          />
        ))}
      </RadioGroup>

      <Box sx={{ mt: 1.5 }}>
        {audience.type === "members" ? (
          <MemberPicker
            value={audience.members}
            onChange={(members) => update({ members })}
          />
        ) : null}

        {audience.type === "tracker" ? (
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Box sx={{ flex: "2 1 280px" }}>
              <SingleOptionPicker
                label="Journey"
                list={trackers}
                value={audience.tracker}
                getLabel={trackerLabel}
                onChange={(tracker) => update({ tracker })}
              />
            </Box>
            <TextField
              select
              label="People"
              value={audience.trackerStatus}
              onChange={(event) => update({ trackerStatus: event.target.value })}
              sx={{ flex: "1 1 200px" }}
            >
              {TRACKER_STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        ) : null}

        {audience.type === "programme" ? (
          <SingleOptionPicker
            label="Programme"
            list={programmes}
            value={audience.programme}
            getLabel={(programme) => programme?.title || ""}
            onChange={(programme) => update({ programme })}
          />
        ) : null}

        {audience.type === "all" ? (
          <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
            Every active member (journey guests are never included).
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}
